import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import Auth from "./pages/Auth";
import AppleAuthCallback from "./pages/AppleAuthCallback";
import GoogleAuthCallback from "./pages/GoogleAuthCallback";
import Dashboard from "./pages/Dashboard";
import DashboardLegacy from "./pages/DashboardLegacy";
import SubmitReview from "./pages/SubmitReview";
import Admin from "./pages/Admin";
import Privacy from "./pages/Privacy";
import Support from "./pages/Support";
import PrivacyChoices from "./pages/PrivacyChoices";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "./components/ErrorBoundary";

// Safe environment variable access with fallback
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
export const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth - ensures token is always sent
axios.interceptors.request.use(
  (config) => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (e) {
      // localStorage might not be available in some contexts
      console.warn('localStorage not available:', e);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration gracefully
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // If we get a 401 on any request (except login), the token has expired
    if (error.response?.status === 401 && !error.config.url?.includes('/auth/login')) {
      // Don't clear token for admin routes - let the component handle the redirect
      if (!error.config.url?.includes('/admin/')) {
        localStorage.removeItem('token');
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const res = await axios.get(`${API}/auth/me`);
          setUser(res.data);
          // Store last login time for session tracking
          localStorage.setItem('lastLoginCheck', Date.now().toString());
        } catch (error) {
          // Only remove token if it's actually invalid (401)
          if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('lastLoginCheck');
          }
          // For other errors (network, server), keep the token and try again later
        }
      }
      setLoading(false);
    };
    
    checkAuth();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-zinc-900">
        <div className="text-yellow-400 text-2xl font-bold">Loading...</div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="App">
        <Toaster position="top-center" richColors />
        <BrowserRouter>
          <Routes>
            {/* Submit Review is now the landing page - accessible to all */}
            <Route path="/" element={<SubmitReview user={user} setUser={setUser} />} />
            {/* Auth page - allow access if session_id is in URL (for Google OAuth callback) */}
            <Route path="/auth" element={<Auth setUser={setUser} user={user} />} />
            {/* Apple Sign In callback */}
            <Route path="/auth/apple/callback" element={<AppleAuthCallback setUser={setUser} />} />
            {/* Google Sign In callback */}
            <Route path="/auth/google/callback" element={<GoogleAuthCallback setUser={setUser} />} />
            <Route path="/dashboard" element={<Dashboard user={user} />} />
            <Route path="/submit-review" element={<Navigate to="/" />} />
            <Route path="/admin" element={user ? <Admin user={user} /> : <Navigate to="/auth" />} />
            {/* Legacy dashboard - parked design, accessible only to admin */}
            <Route path="/dashboard-legacy" element={user?.is_admin ? <DashboardLegacy user={user} /> : <Navigate to="/dashboard" />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/privacy-choices" element={<PrivacyChoices />} />
            <Route path="/support" element={<Support />} />
          </Routes>
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

export default App;